import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { ArrowLeft, TreeEvergreen, Camera, MapPin, Trophy, Upload, Heart, Crown, Coins } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import TreeTokenWidget from "@/components/dashboard/TreeTokenWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [caption, setCaption] = useState("");
  const [treesCount, setTreesCount] = useState(1);
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [file, setFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchPosts();
    fetchLeaderboard();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLon(pos.coords.longitude.toFixed(6));
      }, () => {});
    }
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social/posts?limit=50`, { withCredentials: true });
      setPosts(res.data?.posts || []);
    } catch { /* silent */ }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      setLeaderboard(res.data?.leaderboard || []);
    } catch { /* silent */ }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!file || !lat || !lon) return;
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("lat", lat);
      formData.append("lon", lon);
      formData.append("caption", caption);
      formData.append("trees_count", treesCount);
      await axios.post(`${API}/social/post`, formData, { withCredentials: true });
      setCaption(""); setFile(null); setTreesCount(1);
      alert("Submitted for review. You'll see it live once an admin/moderator approves it.");
      fetchPosts(); fetchLeaderboard();
    } catch (e) {
      alert(`Upload failed: ${e.response?.data?.detail || e.message || "please try again"}`);
    }
    finally { setPosting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="social-page">
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-50">
        <div className="h-12 sm:h-14 flex items-center px-3 sm:px-4 gap-3">
          <Link to="/dashboard" className="text-stone-400 hover:text-white"><ArrowLeft size={18} weight="bold" /></Link>
          <TreeEvergreen size={18} weight="bold" className="text-emerald-400" />
          <span className="font-heading text-sm font-semibold truncate">Tree Planting</span>
          <span className="font-heading text-sm font-semibold hidden sm:inline">Community</span>
          <div className="flex-1" />
        </div>
        <div className="flex gap-1 px-3 pb-2 overflow-x-auto scrollbar-thin">
          {[
            { id: "feed", label: "Feed" }, { id: "map", label: "Map" },
            { id: "post", label: "Plant" }, { id: "leaderboard", label: "Rankings" },
            { id: "tokens", label: "Tokens" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs rounded-lg font-body transition-colors whitespace-nowrap shrink-0 ${tab === t.id ? "bg-emerald-600 text-white" : "text-stone-400 hover:text-white bg-stone-800/40"}`}
              data-testid={`social-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* FEED */}
        {tab === "feed" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.length === 0 ? (
              <p className="text-stone-500 col-span-full text-center py-12">No posts yet. Be the first to plant a tree!</p>
            ) : posts.map((p) => (
              <div key={p.id} className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden" data-testid={`post-${p.id}`}>
                {p.image_path && (
                  <div className="h-48 bg-stone-800 flex items-center justify-center overflow-hidden">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/social/image/${p.image_path}`} alt="Tree"
                      className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-emerald-600/20 flex items-center justify-center">
                      <TreeEvergreen size={14} className="text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-heading font-medium">{p.user_name}</p>
                      <p className="text-[10px] text-stone-500">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-xs text-emerald-400 font-semibold">{p.trees_count} tree{p.trees_count > 1 ? "s" : ""}</p>
                      <p className="text-[10px] text-stone-500">{p.co2_kg}kg CO2/yr</p>
                    </div>
                  </div>
                  {p.caption && <p className="text-sm text-stone-300 font-body mb-2">{p.caption}</p>}
                  <div className="flex items-center gap-1 text-[10px] text-stone-500">
                    <MapPin size={10} /> {p.lat.toFixed(4)}, {p.lon.toFixed(4)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MAP */}
        {tab === "map" && (
          <div className="h-[75vh] rounded-xl overflow-hidden border border-stone-800">
            <MapContainer center={[20, 0]} zoom={3} className="h-full w-full" style={{ background: "#1c1917" }}>
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
              {posts.map((p) => (
                <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={8}
                  pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.6, weight: 2 }}>
                  <Popup>
                    <div className="font-body">
                      <p className="font-heading font-semibold">{p.user_name}</p>
                      <p className="text-xs">{p.trees_count} trees planted</p>
                      <p className="text-xs text-stone-500">{p.caption}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}

        {/* POST */}
        {tab === "post" && (
          <div className="max-w-md mx-auto">
            <div className="bg-stone-900 rounded-xl border border-stone-800 p-6">
              <h2 className="font-heading text-lg font-semibold mb-4">Plant a Tree & Share</h2>
              <form onSubmit={handlePost} className="space-y-4">
                <div>
                  <label className="text-xs text-stone-400 block mb-1">Photo of your planted tree</label>
                  <input type="file" ref={fileRef} accept="image/*" onChange={(e) => setFile(e.target.files[0])}
                    className="hidden" data-testid="social-file-input" />
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="w-full py-8 rounded-xl border-2 border-dashed border-stone-700 hover:border-emerald-600 text-stone-400 hover:text-emerald-400 transition-colors flex flex-col items-center gap-2">
                    <Upload size={24} />
                    <span className="text-xs">{file ? file.name : "Click to upload photo"}</span>
                  </button>
                </div>
                <div>
                  <label className="text-xs text-stone-400 block mb-1">Caption</label>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2}
                    className="w-full bg-stone-800 border border-stone-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                    placeholder="Share your tree planting story..." data-testid="social-caption" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-stone-400 block mb-1">Trees</label>
                    <input type="number" value={treesCount} onChange={(e) => setTreesCount(parseInt(e.target.value) || 1)} min={1}
                      className="w-full bg-stone-800 border border-stone-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none"
                      data-testid="social-trees-count" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-400 block mb-1">Latitude</label>
                    <input type="text" value={lat} onChange={(e) => setLat(e.target.value)}
                      className="w-full bg-stone-800 border border-stone-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none"
                      data-testid="social-lat" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-400 block mb-1">Longitude</label>
                    <input type="text" value={lon} onChange={(e) => setLon(e.target.value)}
                      className="w-full bg-stone-800 border border-stone-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none"
                      data-testid="social-lon" />
                  </div>
                </div>
                <button type="submit" disabled={posting || !file}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="social-post-btn">
                  <TreeEvergreen size={18} weight="bold" />
                  {posting ? "Posting..." : "Share Your Contribution"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* LEADERBOARD */}
        {tab === "leaderboard" && (
          <div className="max-w-lg mx-auto">
            <div className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-800 flex items-center gap-2">
                <Trophy size={18} weight="bold" className="text-amber-400" />
                <span className="font-heading text-sm font-semibold">Top Tree Planters</span>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-stone-500 text-center py-8 text-sm">No rankings yet</p>
              ) : leaderboard.map((u) => (
                <div key={u.email} className="flex items-center gap-4 px-5 py-3 border-b border-stone-800/50 last:border-0" data-testid={`leaderboard-rank-${u.rank}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm ${u.rank <= 3 ? "bg-amber-500/20 text-amber-400" : "bg-stone-800 text-stone-400"}`}>
                    {u.rank <= 3 ? <Crown size={16} weight="bold" /> : u.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-body">{u.name}</p>
                    <p className="text-[10px] text-stone-500">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-heading font-semibold text-emerald-400">{u.trees_planted} trees</p>
                    <p className="text-[10px] text-stone-500">{u.co2_absorbed_kg}kg CO2</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TREE TOKENS */}
        {tab === "tokens" && (
          <div className="max-w-md mx-auto space-y-4" data-testid="social-tab-tokens-content">
            <div className="flex items-center gap-2 mb-2">
              <Coins size={18} weight="duotone" className="text-emerald-400" />
              <h2 className="font-heading text-lg font-semibold">Your TREE Tokens</h2>
            </div>
            <p className="text-xs text-stone-500 mb-3">
              Plant trees → earn TREE tokens → redeem for personal carbon-offset certificates.
            </p>
            <TreeTokenWidget />
          </div>
        )}
      </div>
    </div>
  );
}
